/**
 * SC-5440 Phase 04 — Timeline block drag math.
 *
 * Pure helpers for the drag-to-confirm move: converting the pointer delta
 * into a day offset (dropping on the current start date is the zero case),
 * and building the Block.Edit input the confirm modal sends. The move itself
 * rides the shared shift path from Phase 01 — the server shifts every
 * workout by the same offset and re-publishes a published block (Phase 02) —
 * so the client only computes and sends the new start date.
 */
import { DateTime } from 'luxon';
import { Block } from './types';

/** Whole days a horizontal drop of `deltaX` pixels moves the block. */
export function blockDragDayOffset(
  deltaX: number,
  dateHeaderScale: number,
): number {
  return Math.round(deltaX / dateHeaderScale);
}

/** The block's start date moved by `dayOffset` days. */
export function blockDragNewStartDate(
  currentStartDate: string,
  dayOffset: number,
): string {
  return (
    DateTime.fromISO(currentStartDate).plus({ days: dayOffset }).toISODate() ??
    currentStartDate
  );
}

/**
 * What a block drop decides: nothing when the drop lands on the current
 * start date (no modal, no toast) or outside the timeline rows; otherwise
 * the proposed new start date, which the Confirm New Start Date modal takes
 * over from.
 */
export function blockDropNewStartDate(
  block: Pick<Block, 'startDate'> | undefined,
  over: unknown,
  deltaX: number,
  dateHeaderScale: number,
): string | null {
  if (!block || !over) return null;
  const dayOffset = blockDragDayOffset(deltaX, dateHeaderScale);
  if (dayOffset === 0) return null;
  return blockDragNewStartDate(block.startDate, dayOffset);
}

/**
 * Both dates of a move, shifted by the offset between the current and
 * proposed start dates, so the block keeps its stored duration. A stored
 * proposed start dates, so the block keeps its stored duration. A stored
 * end date can diverge from start + 7*cycles - 1 after manual edits; the
 * shift preserves what is stored, which is also what the server writes
 * (getShiftedEndDate) — never a value recomputed from the layout.
 */
export function blockPendingRenderDates(
  block: Pick<Block, 'startDate' | 'endDate'>,
  proposedStartDate: string,
): { startDate: string; endDate: string } {
  const dayOffset = Math.round(
    DateTime.fromISO(proposedStartDate).diff(
      DateTime.fromISO(block.startDate),
      'days',
    ).days,
  );
  return {
    startDate: proposedStartDate,
    endDate: blockDragNewStartDate(block.endDate, dayOffset),
  };
}

/**
 * The Block.Edit input for a confirmed drag move: every stored field is sent
 * back unchanged except the dates, so the server's shift path — the same one
 * the Edit Block Drawer's save uses — sees a start-date change and nothing
 * else. The end date is the stored end date shifted by the same offset, the
 * exact value the server writes (getShiftedEndDate).
 */
export function buildBlockMoveInput(
  block: Pick<
    Block,
    | 'id'
    | 'name'
    | 'startDate'
    | 'endDate'
    | 'focuses'
    | 'trainingMaxRatio'
    | 'layout'
    | 'autoSchedule'
    | 'allowFlexWorkouts'
  >,
  newStartDate: string,
) {
  const { endDate } = blockPendingRenderDates(block, newStartDate);

  return {
    id: block.id,
    name: block.name,
    focuses: block.focuses.map((focus, index) => ({
      id: focus.id,
      focusId: focus.focusId,
      sortOrder: index,
    })),
    trainingMaxRatio: block.trainingMaxRatio,
    startDate: newStartDate,
    endDate,
    layout: block.layout,
    autoSchedule: block.autoSchedule,
    allowFlexWorkouts: block.allowFlexWorkouts,
  };
}
